<template>
  <Card :title="t('organization.pageViews.title')">
    <template #on-title-right>
      <div class="page-view-stats__period">
        <button type="button"
                class="page-view-stats__period-btn"
                :class="{ 'page-view-stats__period-btn--active': periodMode === 'last12' }"
                @click="periodMode = 'last12'">
          {{ t('organization.pageViews.period') }}
        </button>
        <button type="button"
                class="page-view-stats__period-btn"
                :class="{ 'page-view-stats__period-btn--active': periodMode === 'all' }"
                @click="periodMode = 'all'">
          {{ t('organization.pageViews.periodAll') }}
        </button>
      </div>
    </template>

    <div class="page-view-stats">
      <p class="page-view-stats__total">
        <span class="page-view-stats__total-value">{{ displayedTotal }}</span>
        {{ t('organization.pageViews.total') }} ({{ periodLabel }})
      </p>

      <PageViewBarChart
          :labels="chartLabels"
          :values="chartValues"
          :label="chartLabel"/>
    </div>
  </Card>
</template>

<script lang="ts" setup>
import {computed, onMounted, ref} from "vue";
import {useI18n} from "vue3-i18n";
import Card from "@/components/layout/Card.vue";
import PageViewBarChart from "@/components/charts/PageViewBarChart.vue";
import {useOrganizationService} from "@/inversify.config";
import {ICorporatePageViewStats} from "@/types/corporatePageViewStats";

// Beyond this span the monthly bars become unreadable, so we aggregate by year instead.
const MAX_MONTHLY_SPAN = 24;

// eslint-disable-next-line no-undef
const props = defineProps<{
  organizationId: string;
}>();

const {t} = useI18n();
const organizationService = useOrganizationService();

const stats = ref<ICorporatePageViewStats>();
const periodMode = ref<"last12" | "all">("last12");

onMounted(async () => {
  try {
    // Fetch the full history once (since 2000); the period toggle slices it client-side.
    stats.value = await organizationService.getCorporatePageViewStats(props.organizationId, "2000-01-01");
  } catch {
    stats.value = {total: 0, daily: []};
  }
});

function monthKey(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
}

// Zero-filled buckets from the start of the selected period to now, folded with the API daily counts.
// "last12" is always the last 12 months (monthly). "all" starts at the first recorded view and switches
// to yearly buckets once the span exceeds MAX_MONTHLY_SPAN, so even 10+ years stays readable.
const buckets = computed(() => {
  const daily = stats.value?.daily ?? [];
  const now = new Date();

  let start = new Date(now.getFullYear(), now.getMonth() - 11, 1);
  if (periodMode.value === "all" && daily.length > 0) {
    const earliest = [...daily.map(d => d.date)].sort()[0];
    if (earliest) {
      const earliestStart = new Date(Number(earliest.substring(0, 4)), Number(earliest.substring(5, 7)) - 1, 1);
      if (earliestStart < start)
        start = earliestStart;
    }
  }

  const monthsSpan = (now.getFullYear() - start.getFullYear()) * 12 + (now.getMonth() - start.getMonth()) + 1;
  const byYear = monthsSpan > MAX_MONTHLY_SPAN;
  const counts = new Map<string, number>();

  if (byYear) {
    for (let year = start.getFullYear(); year <= now.getFullYear(); year++)
      counts.set(String(year), 0);
    for (const day of daily) {
      const key = day.date.substring(0, 4);
      if (counts.has(key))
        counts.set(key, (counts.get(key) ?? 0) + day.count);
    }
  } else {
    for (let cursor = start; cursor <= now; cursor = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))
      counts.set(monthKey(cursor), 0);
    for (const day of daily) {
      const key = day.date.substring(0, 7);
      if (counts.has(key))
        counts.set(key, (counts.get(key) ?? 0) + day.count);
    }
  }

  return {byYear, entries: [...counts.entries()]};
});

const chartLabels = computed(() => buckets.value.entries.map(entry => entry[0]));
const chartValues = computed(() => buckets.value.entries.map(entry => entry[1]));
const displayedTotal = computed(() => buckets.value.entries.reduce((sum, entry) => sum + entry[1], 0));
const chartLabel = computed(() =>
    buckets.value.byYear ? t("organization.pageViews.yearlyChart") : t("organization.pageViews.monthlyChart"));
const periodLabel = computed(() =>
    periodMode.value === "all" ? t("organization.pageViews.periodAll") : t("organization.pageViews.period"));
</script>

<style scoped lang="scss">
.page-view-stats__total {
  margin-bottom: 1rem;
}

.page-view-stats__total-value {
  font-size: 2rem;
  font-weight: 700;
  color: #009DE2;
}

.page-view-stats__period {
  display: flex;
  gap: 0.5rem;
}

.page-view-stats__period-btn {
  padding: 0.25rem 0.75rem;
  border: 1px solid #009DE2;
  border-radius: 4px;
  background: transparent;
  color: #009DE2;
  cursor: pointer;
  font-size: 0.85rem;
}

.page-view-stats__period-btn--active {
  background: #009DE2;
  color: #fff;
}
</style>
